import axios from 'axios';

// API base configuration
// Use REACT_APP_API_URL from environment, default to localhost
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_BASE_URL = `${BASE_URL}/api`;

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens, logging, etc.
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle specific error cases
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// =============================================================================
// REFERENCE DATA API
// =============================================================================

export const referenceDataAPI = {
  // Get all districts
  getDistricts: async () => {
    try {
      const response = await api.get('/districts');

      return response.data;
    } catch (error) {
      console.error('❌ Districts API error:', error);
      throw new Error(`Failed to fetch districts: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get townships by district
  getTownshipsByDistrict: async (districtId) => {
    try {
      const response = await api.get(`/townships/${districtId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch townships: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get all townships
  getAllTownships: async () => {
    try {
      const response = await api.get('/townships');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch townships: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get clinics by township
  getClinicsByTownship: async (townshipId) => {
    try {
      const response = await api.get(`/clinics/${townshipId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch clinics: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get all clinics
  getAllClinics: async () => {
    try {
      const response = await api.get('/clinics');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch clinics: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get villages by clinic
  getVillagesByClinic: async (clinicId) => {
    try {
      const response = await api.get(`/villages/${clinicId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch villages: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get all villages
  getAllVillages: async () => {
    try {
      const response = await api.get('/villages');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch villages: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get villages by township
  getVillagesByTownship: async (townshipId) => {
    try {
      const response = await api.get(`/villages?township_id=${townshipId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch villages by township: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get other reference data
  getGender: async () => {
    try {
      const response = await api.get('/gender');

      return response.data;
    } catch (error) {
      console.error('❌ Gender API error:', error);
      throw new Error(`Failed to fetch gender options: ${error.response?.data?.error || error.message}`);
    }
  },

  getOrganizations: async () => {
    try {
      const response = await api.get('/organizations');

      return response.data;
    } catch (error) {
      console.error('❌ Organizations API error:', error);
      throw new Error(`Failed to fetch organizations: ${error.response?.data?.error || error.message}`);
    }
  },

  getEthnicities: async () => {
    try {
      const response = await api.get('/ethnicities');

      return response.data;
    } catch (error) {
      console.error('❌ Ethnicities API error:', error);
      throw new Error(`Failed to fetch ethnicities: ${error.response?.data?.error || error.message}`);
    }
  },

  getReligions: async () => {
    try {
      const response = await api.get('/religions');

      return response.data;
    } catch (error) {
      console.error('❌ Religions API error:', error);
      throw new Error(`Failed to fetch religions: ${error.response?.data?.error || error.message}`);
    }
  },

  getDays: async () => {
    try {
      const response = await api.get('/days');

      return response.data;
    } catch (error) {
      console.error('❌ Days API error:', error);
      throw new Error(`Failed to fetch days: ${error.response?.data?.error || error.message}`);
    }
  },

  getBirthPlaces: async () => {
    try {
      const response = await api.get('/birth-places');

      return response.data;
    } catch (error) {
      console.error('❌ Birth places API error:', error);
      throw new Error(`Failed to fetch birth places: ${error.response?.data?.error || error.message}`);
    }
  },

  getDeliveryTypes: async () => {
    try {
      const response = await api.get('/delivery-types');

      return response.data;
    } catch (error) {
      console.error('❌ Delivery types API error:', error);
      throw new Error(`Failed to fetch delivery types: ${error.response?.data?.error || error.message}`);
    }
  },

  getPositions: async () => {
    try {
      const response = await api.get('/positions');

      return response.data;
    } catch (error) {
      console.error('❌ Positions API error:', error);
      throw new Error(`Failed to fetch positions: ${error.response?.data?.error || error.message}`);
    }
  },

  // Generic reference data operations
  getReferenceData: async (type) => {
    try {
      const response = await api.get(`/reference/${type}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch ${type}: ${error.response?.data?.error || error.message}`);
    }
  },

  createReferenceData: async (type, data) => {
    try {
      const response = await api.post(`/reference/${type}`, data);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create ${type}: ${error.response?.data?.error || error.message}`);
    }
  },

  updateReferenceData: async (type, id, data) => {
    try {
      const response = await api.put(`/reference/${type}/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update ${type}: ${error.response?.data?.error || error.message}`);
    }
  },

  deleteReferenceData: async (type, id) => {
    try {
      const response = await api.delete(`/reference/${type}/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to delete ${type}: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get ethnicity by ID (to check if it's "Other")
  getEthnicityById: async (id) => {
    try {
      const response = await api.get(`/ethnicities/${id}`);

      return response.data;
    } catch (error) {
      console.error('❌ Ethnicity by ID API error:', error);
      throw new Error(`Failed to fetch ethnicity: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get next registration ID for a clinic
  getNextRegistrationId: async (clinicId) => {
    try {
      const response = await api.get(`/next-registration-id/${clinicId}`);

      return response.data;
    } catch (error) {
      console.error('❌ Next Registration ID API error:', error);
      throw new Error(`Failed to fetch next registration ID: ${error.response?.data?.error || error.message}`);
    }
  }
};

// =============================================================================
// BIRTH RECORDS API
// =============================================================================

export const birthRecordsAPI = {
  // Get all birth records with pagination and filters
  getBirthRecords: async (params = {}) => {
    try {
      const response = await api.get('/birth-records', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch birth records: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get single birth record
  getBirthRecord: async (id) => {
    try {
      const response = await api.get(`/birth-records/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch birth record: ${error.response?.data?.error || error.message}`);
    }
  },

  // Create new birth record
  createBirthRecord: async (data) => {
    try {

      const response = await api.post('/birth-records', data);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to create birth record:', error);
      console.error('❌ Validation errors:', error.response?.data?.details);
      
      // If there are validation details, include them in the error message
      if (error.response?.data?.details) {
        const errorDetails = error.response.data.details.join('\n• ');
        throw new Error(`Validation failed:\n• ${errorDetails}`);
      }
      
      throw new Error(`Failed to create birth record: ${error.response?.data?.error || error.message}`);
    }
  },

  // Update birth record
  updateBirthRecord: async (id, data) => {
    try {

      const response = await api.put(`/birth-records/${id}`, data);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to update birth record:', error);
      throw new Error(`Failed to update birth record: ${error.response?.data?.error || error.message}`);
    }
  },

  // Delete birth record
  deleteBirthRecord: async (id) => {
    try {

      const response = await api.delete(`/birth-records/${id}`);

      return response.data;
    } catch (error) {
      console.error('❌ Failed to delete birth record:', error);
      throw new Error(`Failed to delete birth record: ${error.response?.data?.error || error.message}`);
    }
  },

  // Search birth records
  searchBirthRecords: async (searchParams) => {
    try {

      const response = await api.get('/birth-records', { params: searchParams });

      return response.data;
    } catch (error) {
      console.error('❌ Search failed:', error);
      throw new Error(`Search failed: ${error.response?.data?.error || error.message}`);
    }
  }
};

// =============================================================================
// STATISTICS API
// =============================================================================

export const statisticsAPI = {
  // Get comprehensive statistics
  getStatistics: async (params = {}) => {
    try {
      const response = await api.get('/statistics', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch statistics: ${error.response?.data?.error || error.message}`);
    }
  },

  // Export statistics data
  exportStatistics: async (params = {}) => {
    try {
      const response = await api.get('/statistics/export', { 
        params,
        responseType: 'blob' // For file downloads
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to export statistics: ${error.response?.data?.error || error.message}`);
    }
  }
};

// =============================================================================
// REPORTS API
// =============================================================================

export const reportsAPI = {
  // Generate reports
  generateReport: async (params = {}) => {
    try {
      const response = await api.get('/reports', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to generate report: ${error.response?.data?.error || error.message}`);
    }
  },

  // Get monthly report
  getMonthlyReport: async (params = {}) => 
    reportsAPI.generateReport({ ...params, type: 'monthly' }),

  // Get detailed report
  getDetailedReport: async (params = {}) => 
    reportsAPI.generateReport({ ...params, type: 'detailed' }),

  // Get birth defects report
  getBirthDefectsReport: async (params = {}) => 
    reportsAPI.generateReport({ ...params, type: 'birth_defects' }),

  // Get twins report
  getTwinsReport: async (params = {}) => 
    reportsAPI.generateReport({ ...params, type: 'twins' }),

  // Get clinic performance report
  getClinicPerformanceReport: async (params = {}) => 
    reportsAPI.generateReport({ ...params, type: 'clinic_performance' }),

  // Get delivery types report
  getDeliveryTypesReport: async (params = {}) => 
    reportsAPI.generateReport({ ...params, type: 'delivery_types' })
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const utilityAPI = {
  // Test database connection
  testDatabase: async () => {
    try {
      const response = await api.get('/test-database');
      return response.data;
    } catch (error) {
      throw new Error(`Database test failed: ${error.response?.data?.error || error.message}`);
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(`Health check failed: ${error.response?.data?.error || error.message}`);
    }
  }
};

// =============================================================================
// FORM SUBMISSION HELPERS
// =============================================================================

export const formHelpers = {
  // Validate birth record data
  validateBirthRecord: (data) => {
    const errors = {};
    
    // Registration validation
    if (!data.birthRecord?.registration_date) {
      errors.registration_date = 'Registration date is required';
    }
    
    if (!data.birthRecord?.registration_id) {
      errors.registration_id = 'Registration ID is required';
    }
    
    // Baby validation
    if (!data.baby?.name) {
      errors.baby_name = 'Baby name is required';
    }
    
    if (!data.baby?.gender_id) {
      errors.baby_gender = 'Baby gender is required';
    }
    
    if (!data.baby?.date_of_birth) {
      errors.baby_dob = 'Baby date of birth is required';
    }
    
    // Parents validation
    // Father information is optional (nullable)
    
    if (!data.mother?.name) {
      errors.mother_name = 'Mother name is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format birth record data for submission
  formatBirthRecordData: (formData) => {
    return {
      birthRecord: {
        registration_date: formData.registration_date,
        registration_id: formData.registration_id,
        organization_id: formData.organization_id || null,
        district_id: formData.district_id || null,
        township_id: formData.township_id || null,
        clinic_id: formData.clinic_id || null,
        // Birth record details
        delivery_type_id: formData.delivery_type_id || null,
        baby_weight: formData.birth_record_baby_weight ? Number(formData.birth_record_baby_weight) : null,
        baby_height: formData.birth_record_baby_height ? Number(formData.birth_record_baby_height) : null,
        baby_head_circumference: formData.birth_record_baby_head_circumference ? Number(formData.birth_record_baby_head_circumference) : null,
        baby_birth_defected: formData.birth_record_baby_birth_defected || false,
        birth_defect_description: formData.birth_defect_description || null,
        mother_right_thumb_print: formData.mother_right_thumb_print || false,
        father_right_thumb_print: formData.father_right_thumb_print || false,
        baby_right_foot_print: formData.baby_right_foot_print || false,
        delivered_by_name: formData.birth_record_delivered_by_name || null,
        delivered_by_position: formData.birth_record_delivered_by_position || null,
        delivered_by_clinic_name: formData.birth_record_delivered_by_clinic_name || null
      },
      baby: {
        name: formData.baby_name,
        gender_id: formData.baby_gender_id,
        hospital_clinic_id: formData.baby_hospital_clinic_id || null,
        date_of_birth: formData.baby_date_of_birth,
        week_of_day: formData.baby_week_of_day || null,
        birth_time_hour: formData.baby_birth_time_hour || null,
        birth_time_minute: formData.baby_birth_time_minute || null,
        birth_time_ampm: formData.baby_birth_time_ampm || null,
        birth_place_choose: formData.baby_birth_place_choose || null,
        birth_place_other_specify: formData.baby_birth_place_other_specify || null,
        birth_place_district: formData.baby_birth_place_district || null,
        birth_place_township: formData.baby_birth_place_township || null,
        birth_place_village: formData.baby_birth_place_village || null,
        birth_place_other_district: formData.baby_birth_place_other_district || null,
        birth_place_other_township: formData.baby_birth_place_other_township || null,
        birth_place_other_village: formData.baby_birth_place_other_village || null,
        birth_type: formData.baby_birth_type || 'single',
        twin_order: formData.baby_twin_order || null,
        birth_order: formData.baby_birth_order || null
      },
      father: {
        name: formData.father_name,
        national_id: formData.father_national_id || null,
        myanmar_nrc_area_code: formData.father_myanmar_nrc_area_code || null,
        myanmar_nrc_township_code: formData.father_myanmar_nrc_township_code || null,
        myanmar_nrc_type: formData.father_myanmar_nrc_type || null,
        myanmar_nrc_number: formData.father_myanmar_nrc_number || null,
        ethnicity_id: formData.father_ethnicity_id || null,
        ethnicity_other_specify: formData.father_ethnicity_other_specify || null,
        religion_id: formData.father_religion_id || null,
        address: formData.father_address || null
      },
      mother: {
        name: formData.mother_name,
        national_id: formData.mother_national_id || null,
        myanmar_nrc_area_code: formData.mother_myanmar_nrc_area_code || null,
        myanmar_nrc_township_code: formData.mother_myanmar_nrc_township_code || null,
        myanmar_nrc_type: formData.mother_myanmar_nrc_type || null,
        myanmar_nrc_number: formData.mother_myanmar_nrc_number || null,
        ethnicity_id: formData.mother_ethnicity_id || null,
        ethnicity_other_specify: formData.mother_ethnicity_other_specify || null,
        religion_id: formData.mother_religion_id || null,
        address: formData.mother_address || null
      },
      deliveredBy: formData.delivered_by_name ? {
        name: formData.delivered_by_name,
        position_id: formData.delivered_by_position_id || null,
        address: formData.delivered_by_address || null,
        signature: formData.delivered_by_signature || false
      } : null,
      recorder: formData.recorder_name ? {
        name: formData.recorder_name,
        position: formData.recorder_position || null,
        clinic_id: formData.recorder_clinic_id || null,
        clinic_name: formData.recorder_clinic_name || null,
        signature: formData.recorder_signature || false
      } : null
    };
  },

  // Format date for input fields
  formatDateForInput: (dateString) => {
    if (!dateString) return '';

    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // For MySQL datetime strings, extract just the date part
      if (typeof dateString === 'string') {
        const dateOnly = dateString.split('T')[0].split(' ')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
          return dateOnly;
        }
      }

      // If still not in correct format, try to parse without timezone conversion
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }

      // Use UTC methods to avoid timezone shift
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  },

  // Format date for display
  formatDateForDisplay: (dateString) => {
    if (!dateString) return '-';

    try {
      // If it's already in YYYY-MM-DD format, work with it directly
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day); // Month is 0-indexed
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }

      // For MySQL datetime strings, extract just the date part
      if (typeof dateString === 'string') {
        const dateOnly = dateString.split('T')[0].split(' ')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
          const [year, month, day] = dateOnly.split('-');
          const date = new Date(year, month - 1, day); // Month is 0-indexed
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        }
      }

      // Fallback for other formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Date display formatting error:', error);
      return '-';
    }
  }
};

// =============================================================================
// USER MANAGEMENT API
// =============================================================================

export const userManagementAPI = {

  // Get all users (admin only)
  getAllUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch users: ${error.response?.data?.error || error.message}`);
    }
  },

  // Create a new user (admin only)
  createUser: async (userData) => {
    try {
      const response = await api.post('/auth/create-user', userData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create user: ${error.response?.data?.error || error.message}`);
    }
  },

  // Update user (admin only)
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update user: ${error.response?.data?.error || error.message}`);
    }
  },

  // Delete user (admin only)
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to delete user: ${error.response?.data?.error || error.message}`);
    }
  },

  // Reset user password (admin only)
  resetUserPassword: async (userId, newPassword) => {
    try {
      const response = await api.post(`/admin/users/${userId}/reset-password`, { newPassword });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to reset password: ${error.response?.data?.error || error.message}`);
    }
  }

};

// =============================================================================
// DEFAULT EXPORT - Combined API object
// =============================================================================

const apiService = {
  referenceData: referenceDataAPI,
  birthRecords: birthRecordsAPI,
  statistics: statisticsAPI,
  reports: reportsAPI,
  utility: utilityAPI,
  userManagement: userManagementAPI,
  formHelpers,
  
  // Direct axios instance for custom calls
  axios: api,
  
  // Utility methods
  setAuthToken: (token) => {
    if (token) {
      localStorage.setItem('authToken', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common['Authorization'];
    }
  },
  
  getAuthToken: () => {
    return localStorage.getItem('authToken');
  },
  
  clearAuth: () => {
    localStorage.removeItem('authToken');
    delete api.defaults.headers.common['Authorization'];
  }
};

export default apiService;
